import React, {useMemo} from 'react';
import {View} from 'react-native';
import {useSelector} from 'react-redux';
import {useNavigationState} from '@react-navigation/native';
import {get, isNil, noop} from 'lodash';

import PersonListItem from '/component/base/PersonListItem';
import Gutter from '/component/layout/Gutter';
import {useMyTeamsContext} from '/component/screen/report/MyTeamsReport/hooks/useMyTeamsContext';
import {generateOnPressTemOrg} from '/component/screen/report/MyTeamsReport/MyTeamsReport.util';
import TeamOrgReportList from '/component/screen/report/MyTeamsReport/TeamOrgReportList';
import {EVENT_NAMES} from '/enum/tealium.enum';
import {getDateUtilDefaultInstance} from '/util/date.util';
import {getTealiumUtilDefaultInstance} from '/util/tealium';

import CandidateDisposition from '../CandidateDisposition';
import {getCandidateSource} from '../DateAddedComponent/DateAddedComponent.util';

import {styles} from './PeopleReportListItem.style';
import {
  generateOnPress,
  getPersonAvatarEmail,
  getPersonAvatarUrl,
} from './PeopleReportListItem.util';
interface ItemType {
  id: number;
  firstName: string;
  lastName: string;
  [key: string]: any;
}
interface PeopleReportListItemProps {
  alternateItemKey: string | null;
  index: number | null;
  item: ItemType;
  onRecordPress: (item: ItemType) => void;
  personIsCoach: (item: ItemType) => boolean;

  PersonNameComponent: React.ElementType;
  PersonRoleComponent: React.ElementType;
  PersonInfoComponent: React.ElementType;
  CandidateDispositionInfo: React.ElementType;
  PersonBonusIndicatorComponent: React.ElementType;
  NewLabelComponent: React.ElementType;
  AcceptByComponent: React.ElementType;
  AcceptByIconComponent: React.ElementType;

  reportName: string;
  hasHorizontalPadding: boolean;
  isCABReport: boolean;
  isCSBReport: boolean;
  lastPaidRank: string;
  isPeopleToContactSection: boolean;
  screenName: string;
  clientMilestone: string;
  hideAvatar: boolean;
  isCandidateReport: boolean;
  isCenterAlignedEvenlySpaced: boolean;
  tierName: string;
  totalCount: number;
  renderNameElementDirectly?: boolean;
  renderRoleElementDirectly?: boolean;
}

export const PeopleReportListItem: React.FC<PeopleReportListItemProps> = ({
  alternateItemKey,
  personIsCoach = () => false,
  PersonNameComponent,
  PersonRoleComponent,
  PersonInfoComponent,
  CandidateDispositionInfo,
  PersonBonusIndicatorComponent,
  NewLabelComponent,
  hasHorizontalPadding = false,
  reportName = '',
  onRecordPress = noop,
  item = {},
  index = null,
  isPeopleToContactSection = false,
  isCandidateReport = false,
  screenName = '',
  clientMilestone = '',
  hideAvatar = false,
  isCenterAlignedEvenlySpaced = false,
  AcceptByComponent,
  AcceptByIconComponent,
  isCABReport = false,
  isCSBReport = false,
  coachId,
  lastPaidRank = '',
  downlineCoachIds,
  reportData,
  reportParams,
  filtersData,
  fetchMoreReport,
  resetMyTeamsData,
  fetchReport,
  coachCurrentRank,
  coachHighestRank,
  count,
  reportMeta,
  loadMore,
  loadingData,
  previousScreenName,
  currentScreenName,
  onFocus,
  totalCount,
  renderNameElementDirectly = false,
  renderRoleElementDirectly = false,
}: any) => {
  const watchlistData = useMemo(() => {
    if (!Array.isArray(reportData) || !Array.isArray(downlineCoachIds))
      return [];

    return reportData.filter(coach =>
      downlineCoachIds.includes(String(coach.id)),
    );
  }, [reportData, downlineCoachIds]);
  let rowStyle = {...styles.row};
  if (index && index > 0) {
    rowStyle = {...rowStyle, ...styles.rowWithBorder};
  }
  const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
    eventName: isCandidateReport
      ? EVENT_NAMES.profileClick
      : EVENT_NAMES.userClick,
  });

  const itemKey = !isNil(alternateItemKey) ? item[alternateItemKey] : item?.id;

  const Wrapper = hasHorizontalPadding ? Gutter : View;
  const fullName =
    item?.name ||
    [item?.firstName, item?.lastName].filter(Boolean).join(' ').trim();
  const createdTs = item?.createdTs;
  const source = item?.source;
  const dispositionDescription = item?.dispositionDescription;

  const acceptBefore = item?.acceptBefore;
  const candidateAcceptByTime =
    getDateUtilDefaultInstance().getLocalTimeInHours(acceptBefore);
  const formattedAcceptByTime = candidateAcceptByTime?.replace(/^0+/, '');
  const showDay = getDateUtilDefaultInstance().checkDate(
    acceptBefore,
    reportName,
  );

  const candidateAccessibilityLabel = useMemo(() => {
    if (!isCandidateReport) return undefined;

    const segments: string[] = [];
    if (fullName) segments.push(fullName);

    const formattedDate = createdTs
      ? getDateUtilDefaultInstance().formatDate(createdTs, 'MMMM d, yyyy')
      : null;
    if (formattedDate) {
      const sourceName = getCandidateSource(source)?.name || 'Leads Pool';
      segments.push(`Added ${formattedDate} from ${sourceName}`);
    }

    if (dispositionDescription) segments.push(dispositionDescription);

    if (acceptBefore) {
      segments.push(`Accept by ${formattedAcceptByTime} CT ${showDay}`);
    }

    const total = Number(
      get(reportMeta, 'page.total', get(loadingData, 'total', '')),
    );
    if (
      Number.isFinite(index) &&
      index >= 0 &&
      Number.isFinite(total) &&
      total > 0
    ) {
      segments.push(`Item ${index + 1} of ${total}`);
    }

    const accessibilityText = segments.join('. ');
    return accessibilityText || undefined;
  }, [
    acceptBefore,
    createdTs,
    dispositionDescription,
    formattedAcceptByTime,
    fullName,
    index,
    isCandidateReport,
    loadingData,
    reportMeta,
    showDay,
    source,
  ]);

  const {reduxActiveTab, reduxIsViewTeam} = useMyTeamsContext();
  const viewTeamData = useSelector(state => state?.myTeams?.viewTeams || []);

  const {routes} = useNavigationState(state => state);

  if (previousScreenName === 'teams') {
    if (reduxIsViewTeam) {
      if (reduxActiveTab === 'my-teams' && routes.length == 2) {
        previousScreenName = 'my_teams_report';
      } else if (reduxActiveTab === 'my-teams' && routes.length > 2) {
        //for coaches_team_report
        previousScreenName = 'coaches_team_report';
      }
    } else {
      //Noty in View TeaM
      if (reduxActiveTab === 'my-teams') {
        //for my_teams_report WHEN WE COME 1ST TIME IN INFO CARD
        previousScreenName = 'teams';
      } else {
        //for watchList
        previousScreenName = 'my_teams_report';
      }
    }
  }

  return (
    <Wrapper style={rowStyle}>
      {reportName !== 'MY_TEAMS' ? (
        <PersonListItem
          allowFontScaling
          maxFontSizeMultiplier={2}
          index={index}
          loadingData={loadingData}
          reportMeta={reportMeta}
          isCandidateReport={isCandidateReport}
          hideAvatar={clientMilestone !== 'candidate' ? hideAvatar : true}
          readonly={!onRecordPress}
          key={itemKey}
          reportName={reportName}
          isCoach={personIsCoach(item)}
          avatarUrl={getPersonAvatarUrl(item)}
          avatarEmail={getPersonAvatarEmail(item)}
          name={PersonNameComponent && <PersonNameComponent item={item} />}
          role={<PersonRoleComponent item={item} isCABReport={isCABReport} />}
          attribute={
            PersonInfoComponent && (
              <PersonInfoComponent item={item} isCABReport={isCABReport} />
            )
          }
          bonusIndicator={
            PersonBonusIndicatorComponent && (
              <PersonBonusIndicatorComponent
                item={item}
                isCABReport={isCABReport}
              />
            )
          }
          newLabel={NewLabelComponent && <NewLabelComponent item={item} />}
          acceptBy={
            AcceptByComponent && (
              <AcceptByComponent item={item} reportName={reportName} />
            )
          }
          CandidateDispositionInfo={
            CandidateDispositionInfo && <CandidateDisposition item={item} />
          }
          acceptByIcon={
            AcceptByIconComponent && (
              <AcceptByIconComponent item={item} reportName={reportName} />
            )
          }
          onPress={generateOnPress({
            sendEvent,
            personIsCoach,
            item,
            reportName,
            clientMilestone,
            onRecordPress,
            isPeopleToContactSection,
            isCandidateReport,
            screenName,
            lastPaidRank,
          })}
          style={{
            ...(isPeopleToContactSection && styles.clientsToContactBaseStyle),
            ...(isCABReport && {
              base: styles.base,
              role: styles.role,
              bonusIndicator: styles.bonusIndicator,
            }),
            ...(isCSBReport && {base: styles.clientsToContactBaseStyle}),
          }}
          isCSBReport={isCSBReport}
          isCABReport={isCABReport}
          isCenterAlignedEvenlySpaced={
            isPeopleToContactSection ||
            isCandidateReport ||
            isCenterAlignedEvenlySpaced
          }
          fullName={fullName}
          onFocus={onFocus}
          totalCount={totalCount}
          accessibilityLabel={candidateAccessibilityLabel}
          renderNameElementDirectly={renderNameElementDirectly}
          renderRoleElementDirectly={renderRoleElementDirectly}
        />
      ) : (
        <TeamOrgReportList
          onPress={generateOnPressTemOrg({
            sendEvent,
            onRecordPress,
            lastPaidRank,
            coachCurrentRank,
            coachHighestRank,
            previousScreenName,
            currentScreenName,
          })}
          item={
            reduxIsViewTeam && reduxActiveTab === 'watchlist'
              ? watchlistData
              : reduxIsViewTeam
              ? viewTeamData
              : reportData
          }
          reportMeta={reportMeta}
          downlineCoachIds={downlineCoachIds}
          filtersData={filtersData}
          reportParams={reportParams}
          fetchMoreReport={fetchMoreReport}
          resetMyTeamsData={resetMyTeamsData}
          fetchReport={fetchReport}
          count={count}
          loadMore={loadMore}
          coachId={coachId}
          lastPaidRank={lastPaidRank}
          highestAchievedRank={coachHighestRank}
          coachCurrentRank={coachCurrentRank}
          previousScreenName={previousScreenName}
          currentScreenName={currentScreenName}
        />
      )}
    </Wrapper>
  );
};
